// #10 Layout: Stack (a column), Grid (equal tracks, or auto-fit tracks of a least width), Split (main · 320 px side)
// and Flex (a row). Gaps and track counts pass as --mc-* numbers (plan §1.11). A grid of three or more tracks carries
// .mc-gn, so it reflows at ≤ 1180; Split's side drops under the main column at ≤ 1180; Flex wraps at ≤ 1180.

import type { ReactNode } from "react";
import { cx } from "../lib/cx";
import { vars } from "../lib/vars";

type Align = "start" | "center" | "end" | "stretch" | "baseline";
type Justify = "start" | "center" | "end" | "between";

interface Base {
  children?: ReactNode;
  /** Gap in px (Stack, Grid and Split 14; Flex 10). */
  gap?: number | undefined;
  className?: string | undefined;
  id?: string | undefined;
}

export interface StackProps extends Base {
  /** Grows to fill its column (alt .fill). */
  fill?: boolean | undefined;
}

export function Stack({ children, gap, fill, className, id }: StackProps) {
  return <div id={id} className={cx("mc-stack", fill && "mc-fill", className)} style={vars({ "--mc-gap": gap })}>{children}</div>;
}

export interface GridProps extends Base {
  /** Equal tracks (default 2). */
  cols?: number | undefined;
  /** Instead of a count: auto-fit tracks no narrower than this many px. */
  min?: number | undefined;
  align?: Align | undefined;
}

export function Grid({ children, gap, cols = 2, min, align, className, id }: GridProps) {
  return (
    <div
      id={id}
      className={cx("mc-grid", min !== undefined ? "mc-grid-fit" : cols >= 3 && "mc-gn", align && `mc-al-${align}`, className)}
      style={vars({ "--mc-gap": gap, "--mc-cols": min === undefined ? cols : undefined, "--mc-min": min })}
    >
      {children}
    </div>
  );
}

export interface SplitProps extends Omit<Base, "children"> {
  main: ReactNode;
  /** The 320 px side column; it drops under the main column at ≤ 1180. */
  side: ReactNode;
}

export function Split({ main, side, gap, className, id }: SplitProps) {
  return (
    <div id={id} className={cx("mc-split", className)} style={vars({ "--mc-gap": gap })}>
      <div className="mc-stack mc-split-main" style={vars({ "--mc-gap": gap })}>{main}</div>
      <div className="mc-stack mc-split-side" style={vars({ "--mc-gap": gap })}>{side}</div>
    </div>
  );
}

export interface FlexProps extends Base {
  /** Default center. */
  align?: Align | undefined;
  justify?: Justify | undefined;
  /** Wraps at every width (it always wraps at ≤ 1180). */
  wrap?: boolean | undefined;
}

export function Flex({ children, gap, align, justify, wrap, className, id }: FlexProps) {
  return (
    <div
      id={id}
      className={cx("mc-fx", align && align !== "center" && `mc-al-${align}`, justify && `mc-jc-${justify}`, wrap && "mc-wrap", className)}
      style={vars({ "--mc-gap": gap })}
    >
      {children}
    </div>
  );
}
